import { useEffect, useMemo, useState } from "react";
import { api, errorText } from "@/lib/api";
import { ValuePickRow } from "./ValuePickRow";
import { MarketBreakdown } from "./MarketBreakdown";
import { BankrollChart } from "./BankrollChart";
import { pct } from "./valueFormat";

function Stat({ label, value, tone, testid }) {
  return <div className={`vs-stat ${tone || ""}`}><small>{label}</small><b data-testid={testid}>{value}</b></div>;
}

// Record recomputed from a filtered pick set (so a matchup view shows its own record, not the global one).
function recordOf(picks) {
  const wins = picks.filter((p) => p.result === "win").length;
  const losses = picks.filter((p) => p.result === "loss").length;
  const pushes = picks.filter((p) => p.result === "push").length;
  const pending = picks.filter((p) => !p.result).length;
  const profit = picks.reduce((a, p) => a + (p.profit || 0), 0);
  const settled = wins + losses + pushes;
  return { wins, losses, pushes, pending, profit, hit_rate: wins + losses ? wins / (wins + losses) : null, roi: settled ? profit / settled : null };
}

export function ValueHistory({ game, comp }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [scope, setScope] = useState("game");
  useEffect(() => { api.get("/value/history", { params: { comp } }).then((r) => setData(r.data)).catch((e) => setError(errorText(e))); }, [comp]);
  const matchupScope = Boolean(game) && scope === "game";
  const picks = useMemo(() => {
    if (!data) return [];
    if (!matchupScope) return data.picks;
    const set = [game.away_code, game.home_code];
    return data.picks.filter((p) => set.includes(p.team));
  }, [data, matchupScope, game]);
  if (error) return <p className="tv-empty">{error}</p>;
  if (!data) return <p className="tv-empty">Loading history…</p>;
  const s = matchupScope ? recordOf(picks) : data.summary;
  return (
    <div data-testid="value-history">
      {game && (
        <div className="vs-seg vs-hist-scope" style={{ marginBottom: 12 }}>
          <button type="button" className={scope === "game" ? "is-active" : ""} onClick={() => setScope("game")} data-testid="value-hist-scope-game">
            {game.away_code} @ {game.home_code}
          </button>
          <button type="button" className={scope === "all" ? "is-active" : ""} onClick={() => setScope("all")} data-testid="value-hist-scope-all">All games</button>
        </div>
      )}
      <div className="vs-summary">
        <Stat label="Record" value={`${s.wins}-${s.losses}${s.pushes ? `-${s.pushes}` : ""}`} testid="value-hist-record" />
        <Stat label="Hit rate" value={pct(s.hit_rate)} testid="value-hist-hit" />
        <Stat label="Profit (1u)" value={`${s.profit >= 0 ? "+" : ""}${s.profit.toFixed(2)}u`} tone={s.profit >= 0 ? "is-up" : "is-down"} testid="value-hist-profit" />
        <Stat label="ROI" value={s.roi == null ? "—" : `${(s.roi * 100).toFixed(1)}%`} tone={s.roi == null ? "" : s.roi >= 0 ? "is-up" : "is-down"} testid="value-hist-roi" />
        <Stat label="Pending" value={s.pending} testid="value-hist-pending" />
      </div>
      {!picks.length && <p className="tv-empty" data-testid="value-hist-empty">
        {matchupScope ? `No settled value bets yet for ${game.away_code} or ${game.home_code} players.` : "No value picks yet. Scan some odds to start tracking."}
      </p>}
      {!matchupScope && <BankrollChart curves={data.bankroll_curves} />}
      {!matchupScope && <MarketBreakdown rows={data.by_market} />}
      {picks.map((p) => <ValuePickRow key={p.id} pick={p} />)}
    </div>
  );
}
